import { HeartHandshake, Users, ShieldCheck, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { IMAGES, scrollToSection } from "@/lib/site";

const SERVICES = [
    {
        icon: HeartHandshake,
        title: "Crisisbegeleiding Jeugd",
        subtitle: "Jongeren & gezinnen",
        description:
            "Directe ondersteuning wanneer een situatie thuis of op school escaleert. Ik stabiliseer, creëer veiligheid en help het gezin weer grip te krijgen.",
        testid: "service-card-begeleiding",
    },
    {
        icon: Users,
        title: "Crisisbegeleiding LVB",
        subtitle: "Licht verstandelijke beperking",
        description:
            "Rustige en voorspelbare begeleiding voor volwassenen met LVB in crisistijd. De-escalerend, duidelijk en afgestemd op het tempo van de persoon.",
        testid: "service-card-maatschappelijk",
    },
    {
        icon: ShieldCheck,
        title: "Nazorg & Stabilisatie",
        subtitle: "Voorkom een nieuwe crisis",
        description:
            "Na de acute fase bouwen we samen aan structuur, signaleringsplannen en een veilige dagindeling om herhaling te voorkomen.",
        testid: "service-card-structuur",
    },
];

export default function Services() {
    return (
        <section id="diensten" className="py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 lg:px-8">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-terracotta">
                        Diensten
                    </p>
                    <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold leading-snug tracking-tight sm:text-4xl">
                        Crisiszorg voor jeugd en LVB
                    </h2>
                    <p className="mt-4 max-w-xl text-base leading-relaxed text-moss-900/70">
                        Specialistische hulp wanneer alles vastloopt — direct, persoonlijk
                        en zonder wachtlijst.
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-6 lg:grid-cols-6">
                    {SERVICES.map((service, i) => (
                        <Reveal key={service.title} delay={i * 0.12} className="lg:col-span-2">
                            <div
                                data-testid={service.testid}
                                className="group flex h-full flex-col rounded-3xl border border-line bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                            >
                                <div className="flex items-start justify-between">
                                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-moss-100 text-moss-800 transition-colors duration-300 group-hover:bg-terracotta group-hover:text-sand-50">
                                        <service.icon size={24} />
                                    </span>
                                    <span className="font-serif text-2xl italic text-moss-900/20">
                                        0{i + 1}
                                    </span>
                                </div>
                                <h3 className="mt-7 font-serif text-2xl font-semibold tracking-tight">
                                    {service.title}
                                </h3>
                                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">
                                    {service.subtitle}
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-moss-900/70">
                                    {service.description}
                                </p>
                            </div>
                        </Reveal>
                    ))}

                    <Reveal delay={0.1} className="lg:col-span-4">
                        <div className="group relative h-64 overflow-hidden rounded-3xl border border-line lg:h-full lg:min-h-[300px]">
                            <img
                                src={IMAGES.walk}
                                alt="Rustig samen op pad na een crisissituatie"
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-moss-900/70 via-moss-900/10 to-transparent" />
                            <p className="absolute bottom-6 left-6 max-w-sm font-serif text-2xl font-medium italic leading-snug text-sand-50">
                                "Rust brengen wanneer het ertoe doet."
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.2} className="lg:col-span-2">
                        <div className="flex h-full flex-col justify-between rounded-3xl bg-moss-900 p-8 text-sand-50">
                            <p className="font-serif text-2xl font-medium italic leading-snug">
                                In een crisis telt elke minuut — en elke mens.
                            </p>
                            <button
                                onClick={() => scrollToSection("#intake")}
                                data-testid="services-cta-intake"
                                className="group mt-8 flex w-fit items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-semibold text-sand-50 transition-all duration-300 hover:bg-terracotta-dark"
                            >
                                Vraag direct hulp
                                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
